import {
	type Bordereau,
	type Exercice,
	type Mouvement,
	Sens,
} from '@mairistem/comptabilite';
import {
	FilterScope,
	getErrorMessage,
	type UserRole,
} from '@mairistem/finances-utils';
import { newIdentifiant } from '@mairistem/tools';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import BordereauSection from '../../components/Liquidation/LiquidationTreeView/Bordereau/BordereauSection';
import EntiteSection from '../../components/Liquidation/LiquidationTreeView/Entite/EntiteSection';
import CreateMouvementRow from '../../components/Liquidation/LiquidationTreeView/Mouvement/CreateMouvementRow';
import MouvementActionIcon from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementActionIcon';
import MouvementExpanded from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementExpanded';
import MouvementReduce from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementReduce';
import BordereauEtat from '../../constants/liquidation/bordereaux/BordereauEtat';
import liquidationHeight from '../../constants/liquidation/LiquidationHeight';
import TreeNodeType from '../../constants/liquidation/TreeNodeType';
import TreeRowKey from '../../constants/liquidation/TreeRowKey';
import CODE_BHM from '../../constants/mouvements/codeBHM';
import { TYPE_BORDEREAU } from '../../constants/mouvements/typeBordereau';
import type {
	BordereauWithState,
	TreeRow,
} from '../../interfaces/pieces/TreeRow';
import { getBordereaux } from '../../services/piecesComptables/bordereaux/bordereaux';
import getMouvements from '../../services/piecesComptables/mouvements/mouvements';
import type { DemandesRafraichissement } from '../../stores/liquidationTreeviewStore';
import BordereauNouveauState from '../bordereaux/state/BordereauNouveauState';
import BordereauStateFactory from '../bordereaux/state/BordereauStateFactory';
import BaseTreeView, {
	type RenderRowOptions,
	type TreeRowActions,
} from '../pieces/BaseTreeView';
import type TreeIndex from '../pieces/TreeIndex';

interface DefaultNewBordereauCommonData {
	codeBHM: string;
	identifiantEntite: number;
	identifiantExercice: number;
}

type MouvementExtended = Mouvement & {
	isAnotherBordereau?: boolean;
	isNewBordereau?: boolean;
};

/**
 * Pièces par page au chargement d'un bordereau, pages demandées en parallèle. Mesuré sur un bordereau de 5 000
 * pièces : 0,7 s en 20 requêtes, contre 3,4 s en 334 requêtes avec la page par défaut de l'API (15) et 1,9 s en
 * une seule requête (all).
 */
const MOUVEMENTS_PAR_PAGE = 250;

/** Paramètres communs à tous les chargements de bordereaux de l'arbre (liste d'une entité, rafraîchissement). */
const BORDEREAUX_PARAMS = {
	codeBHM: FilterScope.filterIn([
		CODE_BHM.VALUE.BROUILLARD,
		CODE_BHM.VALUE.HISTORISE,
	]),
	journalEtat: BordereauEtat.TRAITEMENT_PES_EN_INSTANCE,
	withJournaux: 'true',
	withMontant: 'true',
	withPiecesCount: 'true',
} as const;

class MouvementTreeView extends BaseTreeView {
	compareChildren(
		nodeType: TreeNodeType,
	): ((a: TreeRow, b: TreeRow) => number) | undefined {
		switch (nodeType) {
			case TreeNodeType.ENTITE:
				return this.orderBordereaux;
			case TreeNodeType.BORDEREAU:
				return this.orderMouvements;
			default:
				return undefined;
		}
	}

	/** Enfants d'une ligne : liste vide si elle n'en a pas, null si le chargement a échoué (déjà signalé). */
	loadData = async (rowData: TreeRow): Promise<TreeRow[] | null> => {
		let data: TreeRow[] | null = null;

		switch (rowData.nodeType) {
			case TreeNodeType.ENTITE:
				data = await this.loadBordereaux(rowData);
				break;
			case TreeNodeType.BORDEREAU:
				data = await this.loadMouvements(rowData);
				break;
			default:
				return null;
		}

		return this.orderData(data, rowData.nodeType);
	};

	getSensOrder = (row: TreeRow) => {
		switch (row?.bordereau?.sens) {
			case Sens.DEPENSE:
				return 3;
			case Sens.RECETTE:
				return 2;
			default:
				return 1;
		}
	};

	orderBordereaux = (a: TreeRow, b: TreeRow): number => {
		const sensA: number = this.getSensOrder(a);
		const sensB: number = this.getSensOrder(b);

		if (sensA !== sensB) {
			return sensB - sensA;
		}

		const stepA: number = a.bordereau.state.step ?? 0;
		const stepB: number = b.bordereau.state.step ?? 0;

		if (stepA !== stepB) {
			return stepA - stepB;
		}

		const numeroA: number = Number(a?.bordereau?.numero) || 0;
		const numeroB: number = Number(b?.bordereau?.numero) || 0;

		return numeroB - numeroA;
	};

	orderMouvements = (a: TreeRow, b: TreeRow): number => {
		const numeroA: number = Number(a?.mouvement?.numero) || 0;
		const numeroB: number = Number(b?.mouvement?.numero) || 0;

		return numeroB - numeroA;
	};

	toBordereauRow = (
		bordereau: BordereauWithState,
		exercice: Exercice,
	): TreeRow => ({
		identifiant: bordereau.identifiant,
		libelle: bordereau.libelle,
		bordereau: {
			...bordereau,
			montant: bordereau.montant ?? '0',
			state: BordereauStateFactory.getState(bordereau as Bordereau, exercice),
		},
		rowKey: TreeRowKey.bordereau(bordereau.identifiant),
		children: [],
		nodeType: TreeNodeType.BORDEREAU,
	});

	toMouvementRow = (mouvement: Mouvement): TreeRow => ({
		identifiant: mouvement.identifiant as number,
		libelle: mouvement.objetPiece,
		mouvement,
		rowKey: TreeRowKey.mouvement(mouvement.identifiant),
		children: null,
		nodeType: TreeNodeType.MOUVEMENT,
	});

	/** Bordereaux d'une entité, précédés des bordereaux de travail ; null si le chargement a échoué. */
	loadBordereaux = async (entiteRow: TreeRow): Promise<TreeRow[] | null> => {
		try {
			const { data: bordereaux } = await getBordereaux(
				new URLSearchParams({
					...BORDEREAUX_PARAMS,
					all: 'true',
					identifiantEntite: entiteRow.identifiant.toString(),
					identifiantExercice: entiteRow.exercice.identifiant.toString(),
					filterOnDate: 'true',
				}),
			);

			const bordereauList: TreeRow[] = [];
			const libelleBordereau: string = 'Bordereau de travail';
			// clé de l'entité dans l'arbre (unique), pas identifiantEntite : deux budgets peuvent partager une entité
			const entiteId = entiteRow.rowKey;

			const defaultNewBordereauData: DefaultNewBordereauCommonData = {
				codeBHM: CODE_BHM.VALUE.BROUILLARD,
				identifiantEntite: this.getExercice(entiteRow)?.identifiant,
				identifiantExercice: this.getExercice(entiteRow)?.identifiantEntite,
			};

			// Ajout des bordereaux de type "Nouveau" par défaut
			for (let i = 0; i < 4; i++) {
				const isAnnulation = i % 2;
				const isMandat = i < 2;

				bordereauList.push({
					identifiant: newIdentifiant(),
					libelle: libelleBordereau,
					bordereau: {
						identifiant: newIdentifiant(),
						sens: isMandat ? Sens.DEPENSE : Sens.RECETTE,
						libelle: libelleBordereau,
						state: new BordereauNouveauState(),
						...(isAnnulation && {
							typePes: isMandat
								? TYPE_BORDEREAU.DEPENSE.ANNUL_REDUC
								: TYPE_BORDEREAU.RECETTE.ANNUL_REDUC,
						}),
						...defaultNewBordereauData,
					},
					rowKey: TreeRowKey.bordereauTravail(
						entiteId,
						isMandat,
						Boolean(isAnnulation),
					),
					children: [
						{
							identifiant: newIdentifiant(),
							libelle: `Créer un ${isMandat ? 'mandat' : 'titre'}`,
							rowKey: TreeRowKey.creationMouvementTravail(
								entiteId,
								isMandat,
								Boolean(isAnnulation),
							),
							nodeType: TreeNodeType.CREATE_MOUVEMENT,
						},
					],
					nodeType: TreeNodeType.CREATE_BORDEREAU,
				});
			}

			const { exercice } = entiteRow;
			bordereaux.forEach((bordereau: Bordereau) => {
				bordereauList.push(this.toBordereauRow(bordereau, exercice));
			});

			return bordereauList;
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des bordereaux'),
			);
			return null;
		}
	};

	/**
	 * Toutes les pièces d'un bordereau. Les pages sont triées par identifiant : sans tri, PostgreSQL ne garantit pas
	 * le même ordre d'une requête à l'autre, et des pièces pourraient manquer ou être en double entre deux pages.
	 */
	fetchMouvementsBordereau = async (
		identifiantBordereau: string,
	): Promise<Mouvement[]> => {
		const pageParams = (page: number) =>
			new URLSearchParams({
				identifiantBordereau,
				page: String(page),
				perPage: String(MOUVEMENTS_PAR_PAGE),
				sort: 'identifiant',
			});

		const { data: firstPage, meta } = await getMouvements(pageParams(1));
		const otherPages = await Promise.all(
			Array.from({ length: (meta?.lastPage ?? 1) - 1 }, (_, i) =>
				getMouvements(pageParams(i + 2)),
			),
		);

		return [...firstPage, ...otherPages.flatMap(({ data }) => data)];
	};

	/** Pièces d'un bordereau (précédées de la ligne de création en brouillard) ; null si le chargement a échoué. */
	loadMouvements = async (bordereauRow: TreeRow): Promise<TreeRow[] | null> => {
		try {
			const allMouvements = await this.fetchMouvementsBordereau(
				bordereauRow.identifiant as string,
			);

			let createRow: TreeRow = null;
			if (bordereauRow?.bordereau?.codeBHM === CODE_BHM.VALUE.BROUILLARD) {
				createRow = {
					identifiant: bordereauRow.identifiant,
					libelle: `Créer un ${bordereauRow.bordereau.sens === Sens.DEPENSE ? 'mandat' : 'titre'}`,
					rowKey: TreeRowKey.creationMouvement(bordereauRow.identifiant),
					nodeType: TreeNodeType.CREATE_MOUVEMENT,
				};
			}

			return [
				...(!isNil(createRow) ? [createRow] : []),
				...allMouvements.map(this.toMouvementRow),
			];
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des mouvements'),
			);
			return null;
		}
	};

	/**
	 * Relit les pièces et bordereaux signalés par la fiche. Une pièce ou un bordereau demandé que l'API ne renvoie
	 * plus a été supprimé (ou ne fait plus partie de l'arbre) : il est retiré. Une pièce qui a changé de bordereau
	 * est déplacée ; un bordereau inconnu de l'arbre (créé depuis la fiche) est ajouté à son entité. Chaque ligne
	 * modifiée est replacée dans l'ordre de l'arbre. Toutes les corrections sont faites dans un seul brouillon : une
	 * seule copie de l'index, quel que soit le nombre de lignes relues.
	 */
	refreshRows = async ({
		pieces,
		piecesSupprimees,
		bordereaux,
	}: DemandesRafraichissement): Promise<(index: TreeIndex) => TreeIndex> => {
		const [mouvements, bordereauxRelus] = await Promise.all([
			pieces.size
				? getMouvements(
						new URLSearchParams({
							identifiant: FilterScope.filterIn([...pieces]),
							all: 'true',
						}),
					).then(({ data }) => data)
				: [],
			bordereaux.size
				? getBordereaux(
						new URLSearchParams({
							...BORDEREAUX_PARAMS,
							identifiant: FilterScope.filterIn([...bordereaux]),
							all: 'true',
						}),
					).then(({ data }) => data)
				: [],
		]);

		return (index: TreeIndex): TreeIndex =>
			index.edit((draft) => {
				// identifiants comparés en nombres : l'API ou la fiche peuvent les donner en texte
				const bordereauxRelusIds = new Set<number>();
				bordereauxRelus.forEach((bordereau: Bordereau) => {
					bordereauxRelusIds.add(Number(bordereau.identifiant));
					const rowKey = TreeRowKey.bordereau(bordereau.identifiant);
					if (draft.has(rowKey)) {
						const exercice = this.getExercice(draft.get(rowKey), draft);
						draft.updateNode(rowKey, (row) => ({
							...this.toBordereauRow(bordereau, exercice),
							children: row.children,
						}));
						draft.reposition(rowKey, this.orderBordereaux);
						return;
					}
					const entite = draft
						.getRoots()
						.find(
							(row) =>
								row.exercice?.identifiant === bordereau.identifiantExercice,
						);
					if (entite) {
						draft.addChild(
							entite.rowKey,
							this.toBordereauRow(bordereau, entite.exercice),
							this.orderBordereaux,
						);
					}
				});
				[...bordereaux]
					.filter((identifiant) => !bordereauxRelusIds.has(Number(identifiant)))
					.forEach((identifiant) => {
						draft.deleteNode(TreeRowKey.bordereau(identifiant));
					});

				const piecesRelues = new Set<number>();
				mouvements.forEach((mouvement: Mouvement) => {
					piecesRelues.add(Number(mouvement.identifiant));
					const row = this.toMouvementRow(mouvement);
					const parentKey = TreeRowKey.bordereau(
						mouvement.identifiantBordereau,
					);
					if (draft.getParentKey(row.rowKey) === parentKey) {
						draft.updateNode(row.rowKey, () => row);
						draft.reposition(row.rowKey, this.orderMouvements);
						return;
					}
					// nouvelle pièce, ou pièce changée de bordereau : retirée de l'ancien, ajoutée au nouveau s'il est chargé
					draft.deleteNode(row.rowKey);
					draft.addChild(parentKey, row, this.orderMouvements);
				});

				[...piecesSupprimees, ...pieces]
					.filter((identifiant) => !piecesRelues.has(Number(identifiant)))
					.forEach((identifiant) => {
						draft.deleteNode(TreeRowKey.mouvement(identifiant));
					});
			});
	};

	getRowHeight = (rowData: TreeRow, expanded: boolean): number => {
		if (!rowData?.nodeType) return liquidationHeight.DEFAULT;

		switch (rowData.nodeType) {
			case TreeNodeType.MOUVEMENT:
				if (expanded) {
					return liquidationHeight.MOUVEMENT_EXPANDED;
				}
				return liquidationHeight.MOUVEMENT_REDUCE;
			case TreeNodeType.CREATE_BORDEREAU:
			case TreeNodeType.BORDEREAU:
				return liquidationHeight.BORDEREAU;
			case TreeNodeType.CREATE_MOUVEMENT:
				return liquidationHeight.CREATE_MOUVEMENT;
			default:
				return liquidationHeight.DEFAULT;
		}
	};

	renderRow = (
		rowData: TreeRow,
		expanded: boolean,
		userRole: UserRole,
		onClick: VoidFunction,
		onDoubleClick: VoidFunction,
		actions: TreeRowActions,
		options: RenderRowOptions,
	) => {
		const { index } = options;
		const exercice: Exercice = this.getExercice(rowData, index);

		switch (rowData.nodeType) {
			case TreeNodeType.MOUVEMENT: {
				const bordereauRow = index.getParent(rowData.rowKey);
				const bordereau: BordereauWithState = bordereauRow?.bordereau;

				/**
				 * Pièce créée par une action (duplication) : ajoutée à ce bordereau, à un autre bordereau de l'arbre, ou à un
				 * bordereau créé pour l'occasion, ajouté à l'entité.
				 */
				const onAddMouvement = (mouvement: MouvementExtended) => {
					const entiteKey = index.getParentKey(bordereauRow?.rowKey);

					if (mouvement.isNewBordereau) {
						// ses pièces seront chargées à son dépliage
						actions.addRow(
							entiteKey,
							this.toBordereauRow(
								{
									...mouvement.bordereau,
									montant: String(mouvement.montant),
								},
								exercice,
							),
						);
						return;
					}

					const destinationRow = mouvement.isAnotherBordereau
						? index
								.getChildren(entiteKey)
								?.find((b) => b.identifiant === mouvement.identifiantBordereau)
						: bordereauRow;
					if (!destinationRow) return;

					const montant = String(
						// l'API renvoie les montants en texte : additionnés, pas concaténés
						Number(destinationRow.bordereau?.montant ?? 0) +
							Number(mouvement.montant ?? 0),
					);
					actions.addRow(destinationRow.rowKey, this.toMouvementRow(mouvement));
					// sur un autre bordereau, ses pièces chargées sont relues : la duplication a pu les renuméroter
					actions.changeRow(
						{
							...destinationRow,
							bordereau: { ...destinationRow.bordereau, montant },
						},
						mouvement.isAnotherBordereau,
					);
				};

				return (
					<div className="mouvement-row">
						{expanded ? (
							<MouvementExpanded
								rowData={rowData}
								onClick={onClick}
								onDoubleClick={onDoubleClick}
								exercice={exercice}
							/>
						) : (
							<MouvementReduce
								rowData={rowData}
								onClick={onClick}
								onDoubleClick={onDoubleClick}
								exercice={exercice}
							/>
						)}
						<MouvementActionIcon
							exercice={exercice}
							bordereau={bordereau}
							mouvement={rowData.mouvement}
							alwaysVisible={expanded}
							userRole={userRole}
							onChange={(mouvement) =>
								actions.changeRow({ ...rowData, mouvement })
							}
							onAdd={onAddMouvement}
							onDelete={() => actions.deleteRow(rowData.rowKey)}
						/>
					</div>
				);
			}
			case TreeNodeType.CREATE_MOUVEMENT:
				return <CreateMouvementRow rowData={rowData} exercice={exercice} />;
			case TreeNodeType.ENTITE:
				return <EntiteSection entite={rowData.exercice?.entite} />;
			case TreeNodeType.BORDEREAU:
			case TreeNodeType.CREATE_BORDEREAU:
				return (
					<BordereauSection
						rowData={rowData}
						exercice={exercice}
						expanded={expanded}
						onChange={(bordereau, shouldRefreshChildren) =>
							actions.changeRow(
								{ ...rowData, bordereau },
								shouldRefreshChildren,
							)
						}
						onDelete={() => actions.deleteRow(rowData.rowKey)}
						filterSens={options?.filterSens as string}
						onAdd={(bordereau) =>
							actions.addRow(
								index.getParentKey(rowData.rowKey),
								this.toBordereauRow(bordereau, exercice),
							)
						}
						userRole={userRole}
					/>
				);
			default:
				return null;
		}
	};

	isRowExpandable = (rowData: TreeRow): boolean =>
		rowData.nodeType === TreeNodeType.MOUVEMENT;
}

export default MouvementTreeView;
